import { useState } from 'react'
import { Box, Flex, Text, VStack, Modal, useT } from '@minetrace/ui'
import { LuShieldCheck } from 'react-icons/lu'
import { m3 } from '../theme'

interface ConsentModalProps {
  open: boolean
  onClose: () => void
  onAccept: () => void
}

export default function ConsentModal({ open, onClose, onAccept }: ConsentModalProps) {
  const { t } = useT()
  const [accepted, setAccepted] = useState(false)

  const handleClose = () => {
    setAccepted(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('shared.consent.title')}
      subtitle={t('shared.consent.subtitle')}
      size="lg"
      footer={
        <>
          <Box
            as="button"
            type="button"
            px={5}
            py={2.5}
            borderRadius="md"
            fontSize="sm"
            fontWeight="600"
            color={m3.onSurfaceVariant}
            bg={m3.surfaceContainerHigh}
            cursor="pointer"
            _hover={{ bg: m3.surfaceContainerHighest }}
            onClick={handleClose}
          >
            {t('shared.consent.cancel')}
          </Box>
          <Box
            as="button"
            type="button"
            px={5}
            py={2.5}
            borderRadius="md"
            fontSize="sm"
            fontWeight="600"
            color={accepted ? m3.onPrimary : m3.outline}
            bg={accepted ? m3.primaryContainer : m3.surfaceContainer}
            cursor={accepted ? 'pointer' : 'not-allowed'}
            opacity={accepted ? 1 : 0.6}
            _hover={accepted ? { opacity: 0.9 } : {}}
            onClick={() => { if (accepted) onAccept() }}
          >
            {t('shared.consent.continue')}
          </Box>
        </>
      }
    >
      <VStack gap={5} alignItems="stretch">
        {/* Icon + intro */}
        <Flex gap={3} alignItems="center">
          <Box p={2} borderRadius="lg" bg={m3.secondaryContainer}>
            <LuShieldCheck size={22} color={m3.primaryContainer} />
          </Box>
          <Text tid="shared.consent.intro" fontSize="sm" color={m3.onSurfaceVariant} />
        </Flex>

        {/* Scrollable legal text */}
        <Box
          bg={m3.surfaceContainerLow}
          border="1px solid"
          borderColor={m3.outlineVariant}
          borderRadius="md"
          p={5}
          maxH="320px"
          overflowY="auto"
        >
          <VStack gap={4} alignItems="stretch">
            <Box>
              <Text tid="shared.consent.purpose" fontSize="sm" fontWeight="700" color={m3.onSurface} mb={1} />
              <Text tid="shared.consent.purposeText" fontSize="sm" color={m3.onSurfaceVariant} lineHeight="tall" />
            </Box>

            <Box>
              <Text tid="shared.consent.data" fontSize="sm" fontWeight="700" color={m3.onSurface} mb={1} />
              <Text tid="shared.consent.dataText" fontSize="sm" color={m3.onSurfaceVariant} lineHeight="tall" />
            </Box>

            <Box>
              <Text tid="shared.consent.transfer" fontSize="sm" fontWeight="700" color={m3.onSurface} mb={1} />
              <Text tid="shared.consent.transferText" fontSize="sm" color={m3.onSurfaceVariant} lineHeight="tall" />
            </Box>

            <Box>
              <Text tid="shared.consent.retention" fontSize="sm" fontWeight="700" color={m3.onSurface} mb={1} />
              <Text tid="shared.consent.retentionText" fontSize="sm" color={m3.onSurfaceVariant} lineHeight="tall" />
            </Box>

            <Box>
              <Text tid="shared.consent.arco" fontSize="sm" fontWeight="700" color={m3.onSurface} mb={1} />
              <Text tid="shared.consent.arcoText" fontSize="sm" color={m3.onSurfaceVariant} lineHeight="tall" />
            </Box>
          </VStack>
        </Box>

        {/* Link to privacy policy */}
        <Text fontSize="xs" color={m3.outline}>
          {t('shared.consent.moreInfo')}{' '}
          <Box
            as="a"
            href="/politica-privacidad"
            target="_blank"
            rel="noopener noreferrer"
            color={m3.primaryContainer}
            fontWeight="600"
            textDecoration="underline"
            _hover={{ color: m3.primary }}
          >
            {t('shared.consent.privacyPolicy')}
          </Box>
          .
        </Text>

        {/* Checkbox */}
        <Flex
          as="label"
          gap={3}
          alignItems="flex-start"
          cursor="pointer"
          p={3}
          borderRadius="md"
          bg={accepted ? 'rgba(1, 45, 29, 0.06)' : 'transparent'}
          border="1px solid"
          borderColor={accepted ? m3.primaryContainer : m3.outlineVariant}
          transition="all 0.15s"
        >
          <Box
            as="input"
            type="checkbox"
            checked={accepted}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccepted(e.target.checked)}
            mt="2px"
            accentColor={m3.primaryContainer}
            cursor="pointer"
            flexShrink={0}
            width="16px"
            height="16px"
          />
          <Text tid="shared.consent.acceptCheckbox" fontSize="sm" color={m3.onSurface} lineHeight="tall" />
        </Flex>
      </VStack>
    </Modal>
  )
}
